import {
  ReactNode,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Column } from '@tanstack/react-table';
import { OverlayPanel } from 'primereact/overlaypanel';
import type { CSSTransitionProps } from 'primereact/csstransition';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import { BmweColumnMeta } from '../../../types';
import {
  filterKindOf,
  normalizeFilterValue,
  toTextFilterValue,
} from '../../../helper';
import { BmweTableFilter } from '../bmweTableFilter';
import { tableStrings } from '../i18n/de-de';
import { ValueTabsFilter, ValueTabsFilterHandle } from './valueTabsFilter';
import styles from './filterPin.module.css';

let activeOverlay: OverlayPanel | null = null;

const columnNameOf = <T,>(column: Column<T>) =>
  String(column.columnDef.header || column.id);

/**
 * Closes an open overlay on the first pointer press outside it and its anchor.
 * Replaces PrimeReact's `dismissable`, which ignores the first outside click
 * after any click inside the panel. Capture phase, so the check runs before
 * clicks inside re-render and detach their targets.
 */
function useCloseOnOutsidePress(
  open: boolean,
  overlayRef: RefObject<OverlayPanel | null>,
  anchorRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        overlayRef.current?.getElement()?.contains(target) ||
        anchorRef.current?.contains(target)
      ) {
        return;
      }
      overlayRef.current?.hide();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, overlayRef, anchorRef]);
}

const VIEWPORT_MARGIN = 8;
const ARROW_GAP = 10;
const EXPANDED_HEIGHT = 450;

function placeOverlay(el: HTMLElement, anchor: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - ARROW_GAP - VIEWPORT_MARGIN;
  const above = rect.top - ARROW_GAP - VIEWPORT_MARGIN;
  const flipped = below < EXPANDED_HEIGHT && above > below;

  el.setAttribute('data-p-overlaypanel-flipped', String(flipped));
  el.classList.toggle('p-overlaypanel-flipped', flipped);
  if (flipped) {
    el.style.top = 'auto';
    el.style.bottom = `${window.innerHeight - rect.top - window.scrollY}px`;
  } else {
    el.style.top = `${rect.bottom + window.scrollY}px`;
    el.style.bottom = 'auto';
  }
  el.style.setProperty(
    '--filter-popup-max-height',
    `${flipped ? above : below}px`,
  );
}

function useStablePlacement(
  open: boolean,
  overlayRef: RefObject<OverlayPanel | null>,
  anchorRef: RefObject<HTMLElement | null>,
) {
  const place = useCallback(() => {
    const el = overlayRef.current?.getElement();
    if (el && anchorRef.current) placeOverlay(el, anchorRef.current);
  }, [overlayRef, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      !!overlayRef.current?.getElement()?.contains(target);
    // Scrolling anything but the popup closes it; that wheel/touch doesn't scroll.
    const closeOnScrollAttempt = (e: Event) => {
      if (isInside(e.target)) return;
      e.preventDefault();
      overlayRef.current?.hide();
    };
    const closeOnScroll = (e: Event) => {
      if (!isInside(e.target)) overlayRef.current?.hide();
    };
    let frame = 0;
    const replace = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    const active = { capture: true, passive: false };
    window.addEventListener('wheel', closeOnScrollAttempt, active);
    window.addEventListener('touchmove', closeOnScrollAttempt, active);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', replace);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', closeOnScrollAttempt, active);
      window.removeEventListener('touchmove', closeOnScrollAttempt, active);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', replace);
    };
  }, [open, place, overlayRef]);

  return useMemo<CSSTransitionProps>(
    // PrimeReact's own timings, as options override them. Deferred, but before
    // paint: PrimeReact re-binds the panel's ref after the transition callbacks.
    () => ({
      timeout: { enter: 120, exit: 100 },
      onEnter: () => queueMicrotask(place),
    }),
    [place],
  );
}

/** A "+" button opening a searchable list of `options`; picking one calls `onPick`. */
export function ColumnPickerMenu({
  options,
  onPick,
  label,
}: {
  options: { label: string; value: string }[];
  onPick: (value: string) => void;
  label: string;
}) {
  const menuRef = useRef<OverlayPanel>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  useCloseOnOutsidePress(open, menuRef, anchorRef);
  const transitionOptions = useStablePlacement(open, menuRef, anchorRef);

  return (
    <>
      <span ref={anchorRef} className={styles.pinAnchor}>
        <Button
          type="button"
          size="small"
          text
          icon="pi pi-plus"
          aria-label={label}
          title={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={(e) => menuRef.current?.toggle(e, anchorRef.current)}
        />
      </span>
      <OverlayPanel
        ref={menuRef}
        dismissable={false}
        transitionOptions={transitionOptions}
        onShow={() => setOpen(true)}
        onHide={() => setOpen(false)}
      >
        <ListBox
          options={options}
          optionLabel="label"
          optionValue="value"
          value={null}
          filter
          filterPlaceholder={tableStrings.filter.columnSearchPlaceholder}
          filterInputProps={{ autoFocus: true }}
          emptyFilterMessage={tableStrings.filter.noColumnFound}
          onChange={(e) => {
            if (e.value == null) return;
            menuRef.current?.hide();
            onPick(e.value as string);
          }}
          aria-label={label}
          className={styles.columnMenu}
          listStyle={{ maxHeight: '20rem' }}
        />
      </OverlayPanel>
    </>
  );
}

/** The filter popup of one column, anchored to `renderTrigger`; edits `value` and hands the result to `onApply`. */
export function ColumnFilterPopup<T>({
  column,
  value,
  onApply,
  renderTrigger,
}: {
  column: Column<T>;
  value: unknown;
  onApply: (value: unknown) => void;
  /** The anchor button; its clicks bubble to the anchor, which toggles the popup. */
  renderTrigger: (open: boolean) => ReactNode;
}) {
  const overlayRef = useRef<OverlayPanel>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  useCloseOnOutsidePress(open, overlayRef, anchorRef);
  const transitionOptions = useStablePlacement(open, overlayRef, anchorRef);

  const toggle = (e: SyntheticEvent) => {
    if (activeOverlay && activeOverlay !== overlayRef.current) {
      activeOverlay.hide();
    }
    overlayRef.current?.toggle(e, anchorRef.current);
  };

  return (
    <>
      <span ref={anchorRef} className={styles.pinAnchor} onClick={toggle}>
        {renderTrigger(open)}
      </span>
      <OverlayPanel
        ref={overlayRef}
        dismissable={false}
        transitionOptions={transitionOptions}
        onShow={() => {
          activeOverlay = overlayRef.current;
          setOpen(true);
        }}
        onHide={() => {
          if (activeOverlay === overlayRef.current) activeOverlay = null;
          setOpen(false);
        }}
      >
        {/* OverlayPanel unmounts its content on hide, so each opening starts a fresh draft. */}
        <FilterPanel
          column={column}
          value={value}
          onApply={onApply}
          onClose={() => overlayRef.current?.hide()}
        />
      </OverlayPanel>
    </>
  );
}

function FilterPanel<T>({
  column,
  value,
  onApply,
  onClose,
}: {
  column: Column<T>;
  value: unknown;
  onApply: (value: unknown) => void;
  onClose: () => void;
}) {
  const meta = column.columnDef.meta as BmweColumnMeta | undefined;
  const columnName = columnNameOf(column);
  const kind = filterKindOf(meta);
  const [draft, setDraft] = useState<unknown>(value);
  const tabsRef = useRef<ValueTabsFilterHandle>(null);

  const apply = () => {
    onApply(normalizeFilterValue(meta, draft));
    onClose();
  };

  const clear = () => {
    onApply(undefined);
    onClose();
  };

  return (
    <div
      className={styles.filterPopupContent}
      // Kept inside the popup: PrimeReact counts bubbled clicks as panel clicks
      // and would then ignore the next click outside.
      onClick={(e) => {
        e.stopPropagation();
        tabsRef.current?.handlePanelClick(e.target as Node);
      }}
    >
      <div className={styles.filterHeader}>
        <h3 className={styles.filterTitle}>
          {tableStrings.filter.filterBy(columnName)}
        </h3>
      </div>
      <div className={styles.filterBody}>
        {kind === 'other' ? (
          <BmweTableFilter column={column} value={draft} onChange={setDraft} />
        ) : (
          <ValueTabsFilter
            ref={tabsRef}
            kind={kind}
            meta={meta}
            value={toTextFilterValue(draft)}
            onChange={setDraft}
            columnName={columnName}
          />
        )}
      </div>
      <div className={styles.filterActions}>
        <Button
          type="button"
          label={tableStrings.filter.clear}
          severity="secondary"
          outlined
          onClick={clear}
          size="small"
        />
        <Button
          type="button"
          label={tableStrings.filter.apply}
          onClick={apply}
          size="small"
        />
      </div>
    </div>
  );
}
